import React from 'react'
import ReactDOM from 'react-dom'
import * as Sentry from '@sentry/browser'
import { addTranslations, setAppLocales, essentialLocales } from '@fs/zion-locale'
import { useExperiment } from '@fs/zion-experiments'
import Root from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { AppLayout } from '@fs/zion-ui'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { DSCProvider, useDSCState } from './components/state/DSCStore'
import App from './DsApp'
import * as serviceWorker from './serviceWorker'
import translations from './locales'
import Feedback from './components/shared/components/Feedback'

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: [
        `ReferenceError: Can't find variable: IntersectionObserver`,
        `SecurityError: The operation is insecure.`,
        `Extension context invalidated.`,
        `UnhandledRejection: Non-Error promise rejection captured with value: EU compliance was not acknowledged by the user.`,
        `TypeError: Object.values(...).flatMap is not a function`,
        'Request failed with status code 401', // Happens when a user session times out
        'ResizeObserver loop limit exceeded', // Benign browser bug in Chrome / Edge on Windows
        'Network Error',
        'AbortError: The transaction was aborted, so the request cannot be fulfilled.',
      ],
    })
  )
}

// For details about loading translations: https://www.familysearch.org/frontier/docs/develop/i18n
addTranslations(translations)

// This is where you pass data from the server to the client using the SERVER_DATA global.
// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const FrontierRoot = () => (
  <React.StrictMode>
    <DSCProvider>
      <AppRoot />
    </DSCProvider>
  </React.StrictMode>
)

const AppRoot = () => {
  const dscState = useDSCState()
  const [newLanguagesEx] = useExperiment('newLanguagesEx')
  newLanguagesEx && setAppLocales(essentialLocales)
  const { header, footer, appLayout } = dscState

  return (
    <Router basename={base}>
      <Root {...header} {...footer} analytics>
        <AppLayout {...appLayout}>
          <App />
          <Feedback />
        </AppLayout>
      </Root>
    </Router>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
